'use client'

import {
    EventsFilters,
    EventsListResponse,
    getEvent,
    getEvents,
    MODULE_COLORS,
    MODULE_LABELS,
    SystemEvent,
} from '@/lib/api/events/events-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdChevronLeft,
    MdChevronRight,
    MdExpandLess,
    MdExpandMore,
    MdFilterList,
    MdHistory,
    MdPerson,
    MdRefresh,
    MdSearch
} from 'react-icons/md'
import { toast } from 'react-toastify'

export default function EventsPage() {
    const { user } = useAuth()
    const { canRead } = useResourcePermissions('security', 'events')

    const [loading, setLoading] = useState(true)
    const [data, setData] = useState<EventsListResponse | null>(null)
    const [filters, setFilters] = useState<EventsFilters>({ page: 1, limit: 30 })
    const [showFilters, setShowFilters] = useState(false)
    const [search, setSearch] = useState('')
    const [expandedId, setExpandedId] = useState<string | null>(null)
    const [detail, setDetail] = useState<SystemEvent | null>(null)
    const [loadingDetail, setLoadingDetail] = useState(false)

    useEffect(() => {
        loadEvents()
    }, [filters])

    const loadEvents = async () => {
        setLoading(true)
        try {
            const params = { ...filters }
            if (search.trim()) params.search = search.trim()
            const response = await getEvents(params)
            if (response.success && response.result) {
                setData(response.result)
            }
        } catch {
            toast.error('Erro ao carregar eventos')
        } finally {
            setLoading(false)
        }
    }

    const loadDetail = async (id: string) => {
        if (expandedId === id) {
            setExpandedId(null)
            setDetail(null)
            return
        }
        setExpandedId(id)
        setLoadingDetail(true)
        try {
            const response = await getEvent(id)
            if (response.success && response.result) {
                setDetail(response.result)
            }
        } catch {
            toast.error('Erro ao carregar detalhes')
        } finally {
            setLoadingDetail(false)
        }
    }

    const handleSearch = () => {
        setFilters({ ...filters, page: 1, search: search.trim() || undefined })
    }

    const formatDate = (date: string) => {
        const d = new Date(date)
        return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
    }

    const events = data?.items || []

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <MdHistory size={28} /> Eventos e Auditoria
                    </h1>
                    <p className="text-sm opacity-60 mt-1">
                        {data ? `${data.total} evento(s) registrado(s)` : 'Carregando...'}
                    </p>
                </div>
                <button className="btn btn-sm btn-ghost" onClick={loadEvents}>
                    <MdRefresh size={16} /> Atualizar
                </button>
            </div>

            {/* Search + Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 flex gap-2">
                    <div className="relative flex-1">
                        <MdSearch size={18} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-40 z-10" />
                        <input
                            type="text"
                            className="input input-bordered input-sm w-full pl-9"
                            placeholder="Buscar por descrição, ação ou entidade..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                        />
                    </div>
                    <button className="btn btn-sm btn-secondary" onClick={handleSearch}>
                        Buscar
                    </button>
                </div>
                <button
                    className="btn btn-sm btn-outline"
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <MdFilterList size={16} /> Filtros
                </button>
            </div>

            {/* Filter Panel */}
            {showFilters && (
                <div className="card bg-base-100 shadow-sm">
                    <div className="card-body p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="form-control">
                                <label className="label py-1"><span className="label-text text-xs">Módulo</span></label>
                                <select
                                    className="select select-bordered select-sm"
                                    value={filters.module || ''}
                                    onChange={e => setFilters({ ...filters, module: e.target.value || undefined, page: 1 })}
                                >
                                    <option value="">Todos</option>
                                    {Object.entries(MODULE_LABELS).map(([key, label]) => (
                                        <option key={key} value={key}>{label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-control">
                                <label className="label py-1"><span className="label-text text-xs">Recurso</span></label>
                                <input
                                    type="text"
                                    className="input input-bordered input-sm"
                                    placeholder="Ex: lead, deal, billing..."
                                    value={filters.resource || ''}
                                    onChange={e => setFilters({ ...filters, resource: e.target.value || undefined, page: 1 })}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label py-1"><span className="label-text text-xs">Ação</span></label>
                                <input
                                    type="text"
                                    className="input input-bordered input-sm"
                                    placeholder="Ex: CREATED, UPDATED..."
                                    value={filters.action || ''}
                                    onChange={e => setFilters({ ...filters, action: e.target.value || undefined, page: 1 })}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end mt-2">
                            <button
                                className="btn btn-xs btn-ghost"
                                onClick={() => {
                                    setFilters({ page: 1, limit: 30 })
                                    setSearch('')
                                }}
                            >
                                Limpar filtros
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div className="flex justify-center py-12">
                    <span className="loading loading-spinner loading-lg" />
                </div>
            )}

            {/* Events List */}
            {!loading && (
                <>
                    {events.length === 0 ? (
                        <div className="text-center py-16 opacity-50">
                            <MdHistory size={56} className="mx-auto mb-3" />
                            <p className="text-lg">Nenhum evento encontrado</p>
                            <p className="text-sm mt-1">Os eventos do sistema aparecem aqui automaticamente</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {events.map(event => (
                                <div key={event.id} className="card bg-base-100 shadow-sm">
                                    <div className="card-body p-4">
                                        {/* Header */}
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-medium truncate">{event.description}</p>
                                                    <div className="flex items-center gap-2 mt-1 text-xs opacity-50">
                                                        {event.user && (
                                                            <span className="flex items-center gap-1">
                                                                <MdPerson size={12} />
                                                                {event.user.name?.split(' ')[0]}{event.user.name?.split(' ').length > 1 ? ` ${event.user.name.split(' ').slice(1).map(n => `${n[0]}.`).join(' ')}` : ''}
                                                            </span>
                                                        )}
                                                        <span>{formatDistanceToNow(new Date(event.createdAt), { addSuffix: true, locale: ptBR })}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                className="btn btn-ghost btn-xs btn-circle shrink-0"
                                                onClick={() => loadDetail(event.id)}
                                            >
                                                {expandedId === event.id ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
                                            </button>
                                        </div>

                                        {/* Badges */}
                                        <div className="flex flex-wrap gap-1.5 mt-2">
                                            <span className={`badge badge-xs ${MODULE_COLORS[event.module] || 'badge-ghost'}`}>
                                                {MODULE_LABELS[event.module] || event.module}
                                            </span>
                                            {event.resource && (
                                                <span className="badge badge-xs badge-outline">{event.resource}</span>
                                            )}
                                            <span className="badge badge-xs badge-ghost font-mono">{event.action}</span>
                                            {event.entityType && event.entityId && (
                                                <span className="badge badge-xs badge-outline opacity-60">
                                                    {event.entityType} #{event.entityId}
                                                </span>
                                            )}
                                            {event.error && (
                                                <span className="badge badge-xs badge-error">Erro</span>
                                            )}
                                        </div>

                                        {/* Detail */}
                                        {expandedId === event.id && (
                                            <div className="mt-3 pt-3 border-t border-base-200 space-y-2 text-xs">
                                                {loadingDetail ? (
                                                    <div className="flex justify-center py-4">
                                                        <span className="loading loading-spinner loading-sm" />
                                                    </div>
                                                ) : detail ? (
                                                    <>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div>
                                                                <span className="opacity-50">Data:</span>
                                                                <span className="ml-2">{formatDate(detail.createdAt)}</span>
                                                            </div>
                                                            <div>
                                                                <span className="opacity-50">Usuário:</span>
                                                                <span className="ml-2">{detail.user?.name || 'Sistema'} {detail.user?.email ? `(${detail.user.email})` : ''}</span>
                                                            </div>
                                                            <div>
                                                                <span className="opacity-50">IP:</span>
                                                                <span className="ml-2 font-mono">{detail.ipAddress || '-'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="opacity-50">Método:</span>
                                                                <span className="ml-2 font-mono">{detail.method || '-'} {detail.url || ''}</span>
                                                            </div>
                                                            {detail.company && (
                                                                <div>
                                                                    <span className="opacity-50">Empresa:</span>
                                                                    <span className="ml-2">{detail.company.name}</span>
                                                                </div>
                                                            )}
                                                            {detail.userAgent && (
                                                                <div className="col-span-2">
                                                                    <span className="opacity-50">User Agent:</span>
                                                                    <span className="ml-2 font-mono text-[10px] opacity-60 break-all">{detail.userAgent}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {detail.error && (
                                                            <div className="p-2 bg-error/10 rounded text-error">
                                                                <span className="font-semibold">Erro:</span> {detail.error}
                                                            </div>
                                                        )}
                                                        {detail.metadata && Object.keys(detail.metadata).length > 0 && (
                                                            <div>
                                                                <span className="opacity-50">Metadados:</span>
                                                                <pre className="mt-1 p-2 bg-base-200 rounded text-[10px] overflow-x-auto">
                                                                    {JSON.stringify(detail.metadata, null, 2)}
                                                                </pre>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : null}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {data && data.totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-4">
                            <button
                                className="btn btn-sm btn-ghost"
                                disabled={data.page <= 1}
                                onClick={() => setFilters({ ...filters, page: data.page - 1 })}
                            >
                                <MdChevronLeft size={18} />
                            </button>
                            <span className="text-sm">
                                Página {data.page} de {data.totalPages}
                            </span>
                            <button
                                className="btn btn-sm btn-ghost"
                                disabled={data.page >= data.totalPages}
                                onClick={() => setFilters({ ...filters, page: data.page + 1 })}
                            >
                                <MdChevronRight size={18} />
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
